import { createRoot } from "react-dom/client";
import { ChakraProvider } from "@chakra-ui/react";
import { BrowserRouter } from "react-router-dom";
import { RecoilRoot } from "recoil";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import "focus-visible/dist/focus-visible";

import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import reportWebVitals from "./reportWebVitals";
import { dynamicConfig } from "./config";
import { theme } from "./theme";
import "./analytics";
import App from "./App";
import { reportError } from "./common/utils/reportError";
import { maxRetries, retryDelay } from "./common/api/config";

if (dynamicConfig.hideLogs) {
  console.log = () => {};
  console.warn = () => {};
  console.error = () => {};
  console.info = () => {};
}

Sentry.init({
  dsn: dynamicConfig.sentryUrl,
  integrations: [new BrowserTracing()],
  tracesSampleRate: 0.2,
  beforeSend(event, hint) {
    try {
      if (event.exception?.values?.some((e) => e.mechanism?.type === "onunhandledrejection")) {
        for (let i = 0; i < event.exception.values.length; i += 1) {
          const { originalException } = hint;

          if (originalException) {
            event.exception.values[i].type = `${originalException.constructor.name} [unhandled rejection]`;
            event.exception.values[i].value = originalException.toString();
          }
        }
      }
    } catch (e) {
      reportError("index.tsx - beforeSend", "Sentry beforeSend onunhandledrejection Error", e as Error);
    }

    return event;
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry(failureCount) {
        if (failureCount < maxRetries) {
          return true;
        }

        return false;
      },
      retryDelay,
    },
  },
});

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error('Root element (id="root") not found');
}

const root = createRoot(rootElement);

root.render(
  <BrowserRouter>
    <ChakraProvider theme={theme} resetCSS>
      <RecoilRoot>
        <QueryClientProvider client={queryClient}>
          <App />
          {dynamicConfig.showReactQueryDevtools && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
      </RecoilRoot>
    </ChakraProvider>
  </BrowserRouter>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    registration.waiting?.postMessage({ type: "SKIP_WAITING" });

    registration.update().then(async () => {
      console.log("Service Worker: Installed new version");
      // window.location.reload();
    });
  },
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
